import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from '@mui/material';
import { ExpandMore, Assignment, Flag, Person, Group as GroupIcon } from '@mui/icons-material';
import { getTaskStatuses } from '../redux/TaskCRUD';
import axiosInstance from '@/setup/axios/axiosInstance';

interface TaskFiltersProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  selectedStatuses: string[];
  onStatusChange: (statusId: string) => void;
  selectedPriorities: string[];
  onPriorityChange: (priorityId: string) => void;
  selectedAssignees: string[];
  onAssigneeChange: (assigneeId: string) => void;
  selectedGroups: string[];
  onGroupChange: (groupId: string) => void;
  dateRange: { start: string; end: string };
  onDateRangeChange: (range: { start: string; end: string }) => void;
  taskStatuses: any[];
  taskPriorities: any[];
  users: any[];
  groups: any[];
  companyId?: string;
  orderId?: string;
  loading?: boolean;
}

const TaskFilters: React.FC<TaskFiltersProps> = React.memo(({
  searchTerm,
  onSearchChange,
  selectedStatuses,
  onStatusChange,
  selectedPriorities,
  onPriorityChange,
  selectedAssignees,
  onAssigneeChange,
  selectedGroups,
  onGroupChange,
  dateRange,
  onDateRangeChange,
  taskStatuses,
  taskPriorities,
  groups,
  companyId,
  orderId,
}) => {
  const [statusOptions, setStatusOptions] = useState<any[]>(Array.isArray(taskStatuses) ? taskStatuses : []);
  const [assigneeEmployees, setAssigneeEmployees] = useState<any[]>([]);

  useEffect(() => {
    setStatusOptions(Array.isArray(taskStatuses) ? taskStatuses : []);
  }, [taskStatuses]);

  useEffect(() => {
    let isMounted = true;

    const fetchStatuses = async () => {
      try {
        const response = await getTaskStatuses();
        const rows = Array.isArray(response?.results) ? response.results : [];
        if (isMounted) {
          setStatusOptions(rows);
        }
      } catch {
        // Keep existing status options from props if API call fails.
      }
    };

    fetchStatuses();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchAssignees = async () => {
      try {
        if (!companyId || !orderId) {
          if (isMounted) {
            setAssigneeEmployees([]);
          }
          return;
        }

        const response = await axiosInstance.get('/tasks/creator-reassign/', {
          params: {
            company: companyId,
            order: orderId,
          },
        });

        const payload = response?.data;
        const rows = Array.isArray(payload?.employees)
          ? payload.employees
          : (Array.isArray(payload)
            ? payload
            : (Array.isArray(payload?.results)
              ? payload.results
              : (Array.isArray(payload?.users)
                ? payload.users
                : [])));

        const mapped = rows
          .map((user: any) => {
            const userId = String(user?.id || user?.user_id || user?.user?.id || '').trim();
            if (!userId) return null;

            const displayName =
              String(user?.name || '').trim() ||
              String(user?.get_display_name || '').trim() ||
              String(user?.display_name || '').trim() ||
              `${String(user?.first_name || '').trim()} ${String(user?.last_name || '').trim()}`.trim() ||
              String(user?.username || '').trim() ||
              String(user?.email || '').trim() ||
              `User ${userId.slice(0, 8)}`;

            return {
              id: userId,
              name: displayName,
              display_name: displayName,
            };
          })
          .filter(Boolean);

        if (isMounted) {
          setAssigneeEmployees(mapped);
        }
      } catch {
        if (isMounted) {
          setAssigneeEmployees([]);
        }
      }
    };

    fetchAssignees();

    return () => {
      isMounted = false;
    };
  }, [companyId, orderId]);

  // State to manage accordion expanded state - load from localStorage
  const [expandedAccordions, setExpandedAccordions] = useState(() => {
    try {
      const saved = localStorage.getItem('taskFiltersAccordionState');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.warn('Failed to load accordion state from localStorage');
    }
    return {
      statuses: true,
      priorities: false,
      assignees: false,
      groups: false,
      dateRange: false
    };
  });

  // Handler for accordion expansion
  const handleAccordionChange = useCallback((panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordions((prev: typeof expandedAccordions) => {
      const newState = {
        ...prev,
        [panel]: isExpanded
      };
      // Save to localStorage
      try {
        localStorage.setItem('taskFiltersAccordionState', JSON.stringify(newState));
      } catch (e) {
        console.warn('Failed to save accordion state to localStorage');
      }
      return newState;
    });
  }, []);

  // Memoized handlers for checkbox changes
  const handleStatusChange = useCallback((statusId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    onStatusChange(statusId);
  }, [onStatusChange]);

  const handlePriorityChange = useCallback((priorityId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    onPriorityChange(priorityId);
  }, [onPriorityChange]);

  const handleAssigneeChange = useCallback((assigneeId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    onAssigneeChange(assigneeId);
  }, [onAssigneeChange]);

  const handleGroupChange = useCallback((groupId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    onGroupChange(groupId);
  }, [onGroupChange]);

  

  

  // Single shared accordion section for all filter groups
  const FilterAccordion = React.memo(({ panel, icon, title, expanded, onToggle, children }: {
    panel: string;
    icon: React.ReactNode;
    title: string;
    expanded: boolean;
    onToggle: (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
    children: React.ReactNode;
  }) => (
    <Accordion
      expanded={expanded}
      onChange={onToggle(panel)}
      elevation={0}
      sx={{ mb: 1, '&:before': { display: 'none' }, '&.Mui-expanded': { margin: '0 0 8px 0' } }}
    >
      <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon}
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{title}</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0, pt: 1 }}>
        <Stack spacing={1}>{children}</Stack>
      </AccordionDetails>
    </Accordion>
  ));

  // Single shared checkbox component for all filter items
  const FilterCheckbox = React.memo(({ label, isChecked, onChange, italic }: {
    label: string;
    isChecked: boolean;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    italic?: boolean;
  }) => (
    <FormControlLabel
      control={
        <Checkbox
          checked={isChecked}
          onChange={onChange}
          size="small"
          sx={{ color: 'primary.main', marginRight: 1, '&.Mui-checked': { color: 'primary.main' } }}
        />
      }
      label={
        <Typography variant="body2" sx={{ fontSize: '0.875rem', fontStyle: italic ? 'italic' : 'normal' }}>
          {label}
        </Typography>
      }
      sx={{
        margin: 0,
        '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: italic ? 'text.secondary' : 'text.primary' }
      }}
    />
  ));

  // Memoized statuses list
  const statusesList = useMemo(() => {
    if (statusOptions.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
          Không có trạng thái nào
        </Typography>
      );
    }
    return statusOptions.map((status) => (
      <FilterCheckbox
        key={status.id}
        label={status.name}
        isChecked={selectedStatuses.includes(status.id)}
        onChange={handleStatusChange(status.id)}
      />
    ));
  }, [statusOptions, selectedStatuses, handleStatusChange]);

  // Memoized priorities list
  const prioritiesList = useMemo(() => {
    if (taskPriorities.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
          Không có độ ưu tiên nào
        </Typography>
      );
    }
    return taskPriorities.map((priority) => (
      <FilterCheckbox
        key={priority.id}
        label={priority.name}
        isChecked={selectedPriorities.includes(priority.id)}
        onChange={handlePriorityChange(priority.id)}
      />
    ));
  }, [taskPriorities, selectedPriorities, handlePriorityChange]);

  // Memoized assignees list
  const assigneesList = useMemo(() => {
    const validUsers = assigneeEmployees.filter(user =>
      user && user.id && (user.first_name || user.last_name || user.display_name || user.name)
    );

    const userCheckboxes = validUsers.map((user) => {
      const label =
        user.display_name ||
        `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
        user.name || user.username || 'Unknown User';
      return (
        <FilterCheckbox
          key={user.id}
          label={label}
          isChecked={selectedAssignees.includes(user.id)}
          onChange={handleAssigneeChange(user.id)}
        />
      );
    });

    const unassignedCheckbox = (
      <FilterCheckbox
        key="unassigned"
        label="Chưa phân bổ"
        isChecked={selectedAssignees.includes('unassigned')}
        onChange={handleAssigneeChange('unassigned')}
        italic
      />
    );

    if (validUsers.length === 0) {
      return (
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
            Không có người gán nào
          </Typography>
          {unassignedCheckbox}
        </Stack>
      );
    }

    return [...userCheckboxes, unassignedCheckbox];
  }, [assigneeEmployees, selectedAssignees, handleAssigneeChange]);

  const groupsList = useMemo(() => {
    const validGroups = groups.filter(group =>
      group && group.id && (group.display_name || group.name)
    );

    if (validGroups.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
          Không có nhóm nào
        </Typography>
      );
    }

    return validGroups.map((group) => (
      <FilterCheckbox
        key={`group-${group.id}`}
        label={group.display_name || group.name || 'Unknown Group'}
        isChecked={selectedGroups.includes(group.id)}
        onChange={handleGroupChange(group.id)}
      />
    ));
  }, [groups, selectedGroups, handleGroupChange]);

  return (
    <Paper 
      elevation={0}
      sx={{ 
        width: 260, 
        height: 'fit-content',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper'
      }}
    >
      <Box sx={{ p: 2 }}>
        <FilterAccordion panel="statuses" icon={<Assignment sx={{ fontSize: 18, color: 'primary.main' }} />} title="Trạng thái" expanded={expandedAccordions.statuses} onToggle={handleAccordionChange}>
          {statusesList}
        </FilterAccordion>

        <FilterAccordion panel="priorities" icon={<Flag sx={{ fontSize: 18, color: 'primary.main' }} />} title="Độ ưu tiên" expanded={expandedAccordions.priorities} onToggle={handleAccordionChange}>
          {prioritiesList}
        </FilterAccordion>

        <FilterAccordion panel="assignees" icon={<Person sx={{ fontSize: 18, color: 'primary.main' }} />} title="Người gán" expanded={expandedAccordions.assignees} onToggle={handleAccordionChange}>
          {assigneesList}
        </FilterAccordion>

        <FilterAccordion panel="groups" icon={<GroupIcon sx={{ fontSize: 18, color: 'primary.main' }} />} title="Nhóm" expanded={expandedAccordions.groups} onToggle={handleAccordionChange}>
          {groupsList}
        </FilterAccordion>

        {/* Date Range Filter */}
        {/* <Accordion 
          expanded={expandedAccordions.dateRange}
          onChange={handleAccordionChange('dateRange')}
          elevation={0}
          sx={{ 
            mb: 1,
            borderRadius: 2,
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{ 
              minHeight: 48,
              '&.Mui-expanded': { minHeight: 48 }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DateRange sx={{ fontSize: 18, color: 'primary.main' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Khoảng thời gian
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0, pt: 1 }}>
            <Stack spacing={2}>
              <LabeledField label="Từ" labelWidth={60}>
                <DatePicker
                  value={dateRange.start ? new Date(dateRange.start) : null}
                  onChange={handleStartDateChange}
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                    },
                  }}
                />
              </LabeledField>
              <LabeledField label="Đến" labelWidth={60}>
                <DatePicker
                  value={dateRange.end ? new Date(dateRange.end) : null}
                  onChange={handleEndDateChange}
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                    },
                  }}
                />
              </LabeledField>
              {(dateRange.start || dateRange.end) && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onDateRangeChange({ start: '', end: '' })}
                  sx={{ 
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    py: 0.5,
                    mt: 1
                  }}
                >
                  Xóa bộ lọc ngày
                </Button>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion> */}
      </Box>
    </Paper>
  );
}, (prevProps, nextProps) => {
  // Only re-render if props actually changed
  const propsChanged = Object.keys(prevProps).some(key => {
    if (key === 'users' || key === 'groups' || key === 'taskStatuses' || key === 'taskPriorities') {
      return JSON.stringify((prevProps as any)[key]) !== JSON.stringify((nextProps as any)[key]);
    }
    return (prevProps as any)[key] !== (nextProps as any)[key];
  });
  
  return !propsChanged;
});

TaskFilters.displayName = 'TaskFilters';

export default TaskFilters;
